import { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { LogtoProvider, LogtoConfig, useLogto } from '@logto/react';
import { Shield, Zap, LineChart, FolderOpen, Sparkles, Activity, Target, PieChart, TrendingUp, Home, BarChart3, ChevronRight, AlertCircle, RefreshCw } from 'lucide-react';
import { ConfigForm } from './components/ConfigForm';
import { DataUploadSection } from './components/Sections';
import { ResultsView } from './components/ResultsView';
import { ErrorBoundary } from './components/ErrorBoundary';
import { ModelBuilder } from './components/ModelBuilder';
import { Callback } from './pages/Callback';
import { API_ENDPOINTS } from './config/apiConfig';

type WorkflowStep = 'landing' | 'model-builder' | 'upload' | 'config' | 'results';


const config: LogtoConfig = {
  endpoint: process.env.REACT_APP_LOGTO_ENDPOINT || 'https://mtvcab.logto.app/',
  appId: process.env.REACT_APP_LOGTO_APP_ID || 'brlsub4lmbgejvk45viy8',
};

console.log('[LogtoProvider Config] Initializing with:', {
  endpoint: config.endpoint,
  appId: config.appId,
  currentOrigin: window.location.origin,
  fullRedirectUri: `${window.location.origin}/callback`
});

function Dashboard() {
  const { signIn, signOut, isAuthenticated } = useLogto();

  const handleSignIn = () => {
    const redirectUri = `${window.location.origin}/callback`;
    console.log('Auth: Calling signIn with redirectUri:', redirectUri);
    signIn(redirectUri);
  };

  const handleSignOut = () => {
    const redirectUri = window.location.origin;
    console.log('Auth: Calling signOut with redirectUri:', redirectUri);
    signOut(redirectUri);
  };

  const [backendStatus, setBackendStatus] = useState<string>('Checking...');
  const [isHealthy, setIsHealthy] = useState<boolean>(false);
  const [workflowStep, setWorkflowStep] = useState<WorkflowStep>('landing');
  const [dataFileId, setDataFileId] = useState<string>('');
  const [dataColumns, setDataColumns] = useState<string[]>([]);
  const [currentJobId, setCurrentJobId] = useState<string | null>(null);

  const steps: WorkflowStep[] = ['landing', 'model-builder', 'results'];

  useEffect(() => {
    checkBackendHealth();
    const interval = setInterval(checkBackendHealth, 30000);
    return () => clearInterval(interval);
  }, []);

  const checkBackendHealth = async () => {
    try {
      const response = await fetch(API_ENDPOINTS.SYSTEM.HEALTH);
      const data = await response.json();
      setBackendStatus(`v${data.version}`);
      setIsHealthy(true);
    } catch (error) {
      setBackendStatus('Offline');
      setIsHealthy(false);
    }
  };



  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-xl border-b border-slate-200/60">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-600 to-purple-700 flex items-center justify-center shadow-lg shadow-violet-500/25">
                <BarChart3 className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-slate-900 tracking-tight">Qistrel</h1>
                <p className="text-xs text-slate-500">Marketing Mix Modeling</p>
              </div>
            </div>

            {/* Right side */}
            <div className="flex items-center gap-3">
              <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium ${isHealthy ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-rose-50 text-rose-700 border border-rose-200'}`}>
                <div className={`w-1.5 h-1.5 rounded-full ${isHealthy ? 'bg-emerald-500' : 'bg-rose-500'}`} />
                {backendStatus}
              </div>
              {isAuthenticated ? (
                <button
                  onClick={handleSignOut}
                  className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg text-sm font-medium transition-colors"
                >
                  Sign Out
                </button>
              ) : (
                <button
                  onClick={handleSignIn}
                  className="px-4 py-2 bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-500 hover:to-purple-500 text-white rounded-lg text-sm font-medium transition-all shadow-md shadow-violet-500/25"
                >
                  Sign In
                </button>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Progress Bar - Only show when not on landing page */}
      {workflowStep !== 'landing' && (
        <div className="bg-white border-b border-slate-200">
          <div className="max-w-7xl mx-auto px-6 py-3">
            <div className="flex items-center gap-6">
              {steps.filter(s => s !== 'landing').map((step, idx) => (
                <div
                  key={step}
                  className={`flex items-center gap-2 ${workflowStep === step ? 'text-violet-600' : 'text-slate-400'}`}
                >
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center text-sm font-semibold ${
                    workflowStep === step
                      ? 'bg-violet-600 text-white'
                      : steps.indexOf(workflowStep) > steps.indexOf(step)
                        ? 'bg-emerald-100 text-emerald-600'
                        : 'bg-slate-100 text-slate-500'
                  }`}>
                    {idx + 1}
                  </div>
                  <span className="text-sm font-medium capitalize">{step.replace('-', ' ')}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="flex-grow">
        {/* Landing Page */}
        {workflowStep === 'landing' && (
          <div>
            {/* Hero Section */}
            <section className="relative overflow-hidden">
              {/* Background decoration */}
              <div className="absolute inset-0 bg-gradient-to-br from-violet-50/50 via-white to-purple-50/50" />
              <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[800px] bg-gradient-to-br from-violet-400/10 to-purple-400/10 rounded-full blur-3xl" />

              <div className="relative max-w-4xl mx-auto px-6 py-24 text-center">
                {/* Badge */}
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-violet-100 text-violet-700 text-sm font-medium mb-8">
                  <Sparkles className="w-4 h-4" />
                  <span>Privacy-First Marketing Analytics</span>
                </div>

                {/* Heading */}
                <h1 className="text-5xl md:text-6xl font-bold text-slate-900 tracking-tight mb-6">
                  Transform Marketing Data into
                  <span className="block mt-2 pb-2 bg-gradient-to-r from-violet-600 via-purple-600 to-violet-600 bg-clip-text text-transparent">
                    Strategic Decisions
                  </span>
                </h1>

                {/* Subheading */}
                <p className="text-xl text-slate-600 max-w-2xl mx-auto mb-12 leading-relaxed">
                  Build powerful Marketing Mix Models through a guided, conversational experience.
                  Understand channel impact, optimize budget allocation, and drive ROI.
                </p>

                {/* CTA Button */}
                {isHealthy ? (
                  isAuthenticated ? (
                    <button
                      onClick={() => setWorkflowStep('model-builder')}
                      className="group inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-500 hover:to-purple-500 text-white text-lg font-semibold rounded-2xl shadow-xl shadow-violet-500/30 hover:shadow-2xl hover:shadow-violet-500/40 transition-all duration-300 hover:-translate-y-1"
                    >
                      <BarChart3 className="w-6 h-6" />
                      Build a Model
                      <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center group-hover:bg-white/30 transition-colors">
                        <ChevronRight className="w-5 h-5" />
                      </div>
                    </button>
                  ) : (
                    <button
                      onClick={handleSignIn}
                      className="group inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-500 hover:to-purple-500 text-white text-lg font-semibold rounded-2xl shadow-xl shadow-violet-500/30 hover:shadow-2xl hover:shadow-violet-500/40 transition-all duration-300 hover:-translate-y-1"
                    >
                      <Shield className="w-6 h-6" />
                      Sign In to Get Started
                      <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center group-hover:bg-white/30 transition-colors">
                        <ChevronRight className="w-5 h-5" />
                      </div>
                    </button>
                  )
                ) : (
                  <div className="inline-flex flex-col items-center gap-4 p-6 bg-amber-50 border border-amber-200 rounded-2xl">
                    <div className="flex items-center gap-2 text-amber-700 font-medium">
                      <AlertCircle className="w-5 h-5" />
                      Backend Not Available
                    </div>
                    <p className="text-sm text-amber-600 max-w-md">
                      The backend API is not running. Please ensure the API server is running.
                    </p>
                    <button
                      onClick={checkBackendHealth}
                      className="flex items-center gap-2 px-4 py-2 bg-amber-100 hover:bg-amber-200 text-amber-800 rounded-lg text-sm font-medium transition-colors"
                    >
                      <RefreshCw className="w-4 h-4" />
                      Check Again
                    </button>
                  </div>
                )}

                {/* Trust indicators */}
                <div className="flex items-center justify-center gap-8 mt-12 text-sm text-slate-500">
                  <div className="flex items-center gap-2">
                    <Shield className="w-4 h-4 text-emerald-500" />
                    <span>GDPR Compliant</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Zap className="w-4 h-4 text-violet-500" />
                    <span>Evolutionary Optimization</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Target className="w-4 h-4 text-amber-500" />
                    <span>Actionable Insights</span>
                  </div>
                </div>
              </div>
            </section>

            {/* Features Grid */}
            <section className="py-20 bg-white border-y border-slate-100 overflow-hidden">
              <div className="max-w-7xl mx-auto px-8">
                <div className="text-center mb-16">
                  <h2 className="text-3xl font-bold text-slate-900 mb-4">
                    Why Industry Leaders Choose Qistrel
                  </h2>
                  <p className="text-lg text-slate-600 max-w-2xl mx-auto">
                    Precision-engineered analytics that transform marketing data into strategic advantage.
                  </p>
                </div>

                <div className="grid md:grid-cols-3 gap-8">
                  {[
                    {
                      icon: Shield,
                      title: 'Privacy Compliance',
                      description: 'Zero PII exposure risk. Your data stays on your infrastructure—fully GDPR and CCPA compliant by design.',
                      gradient: 'from-emerald-500 to-teal-500',
                    },
                    {
                      icon: Zap,
                      title: 'Evolutionary Optimization',
                      description: 'Intelligent hyperparameter tuning with warm-start capabilities for rapid model iteration.',
                      gradient: 'from-violet-500 to-purple-500',
                    },
                    {
                      icon: LineChart,
                      title: 'Attribution Clarity',
                      description: 'Decompose contributions across channels with statistically robust confidence intervals.',
                      gradient: 'from-amber-500 to-orange-500',
                    },
                  ].map((feature, idx) => (
                    <div
                      key={idx}
                      className="group relative p-8 rounded-2xl bg-white border border-slate-200 hover:border-slate-300 transition-all duration-300 hover:shadow-xl hover:-translate-y-1"
                    >
                      <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${feature.gradient} flex items-center justify-center mb-6 shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                        <feature.icon className="w-7 h-7 text-white" />
                      </div>
                      <h3 className="text-xl font-bold text-slate-900 mb-3">{feature.title}</h3>
                      <p className="text-slate-600 leading-relaxed">{feature.description}</p>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            {/* Process Section */}
            <section className="py-20">
              <div className="max-w-7xl mx-auto px-8">
                <div className="text-center mb-16">
                  <h2 className="text-3xl font-bold text-slate-900 mb-4">
                    Three Steps to Marketing Intelligence
                  </h2>
                  <p className="text-lg text-slate-600">
                    From raw data to actionable insights in minutes, not months.
                  </p>
                </div>

                <div className="relative">
                  {/* Connector line behind cards */}
                  <div className="hidden md:block absolute top-[72px] left-[16.6%] right-[16.6%] h-0.5 bg-gradient-to-r from-violet-200 via-purple-200 to-violet-200" />

                  <div className="grid md:grid-cols-3 gap-12 relative">
                    {[
                      { step: 1, title: 'Build', desc: 'Answer simple questions to configure your model through our guided conversation', icon: Sparkles },
                      { step: 2, title: 'Analyze', desc: 'Our AI optimizes hyperparameters and trains your Marketing Mix Model', icon: Activity },
                      { step: 3, title: 'Act', desc: 'View insights, understand channel impact, and optimize budget allocation', icon: Target },
                    ].map((item) => (
                      <div key={item.step} className="relative flex flex-col items-center text-center group">
                        {/* Step number badge */}
                        <div className="absolute -top-3 z-10 w-7 h-7 rounded-full bg-gradient-to-br from-violet-500 to-purple-600 text-white text-xs font-bold flex items-center justify-center shadow-lg shadow-violet-500/30 ring-4 ring-white">
                          {item.step}
                        </div>

                        {/* Icon container */}
                        <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-slate-800 to-slate-900 flex items-center justify-center shadow-xl group-hover:scale-105 group-hover:shadow-2xl transition-all duration-300">
                          <item.icon className="w-9 h-9 text-white" />
                        </div>

                        {/* Text */}
                        <h3 className="text-lg font-bold text-slate-900 mt-5 mb-2">{item.title}</h3>
                        <p className="text-sm text-slate-500 max-w-[200px]">{item.desc}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </section>

            {/* Stats Section */}
            <section className="py-16 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
              <div className="max-w-7xl mx-auto px-8">
                <div className="grid md:grid-cols-4 gap-8">
                  {[
                    { value: '46', label: 'API Endpoints', icon: Zap },
                    { value: '130', label: 'Core Modules', icon: PieChart },
                    { value: '30K', label: 'Lines of Code', icon: LineChart },
                    { value: '978', label: 'Unit Tests', icon: TrendingUp },
                  ].map((stat, idx) => (
                    <div key={idx} className="text-center">
                      <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-white/10 mb-4">
                        <stat.icon className="w-6 h-6 text-violet-400" />
                      </div>
                      <p className="text-4xl font-bold text-white mb-2">{stat.value}</p>
                      <p className="text-slate-400 font-medium">{stat.label}</p>
                    </div>
                  ))}
                </div>
              </div>
            </section>
          </div>
        )}

        {/* Model Builder Step - Conversational Interface */}
        {workflowStep === 'model-builder' && (
          <div className="h-[calc(100vh-180px)] fade-in">
            <ErrorBoundary>
              <ModelBuilder
                onAnalysisComplete={(jobId) => {
                  setCurrentJobId(jobId);
                  setWorkflowStep('results');
                }}
                onBack={() => setWorkflowStep('landing')}
              />
            </ErrorBoundary>
          </div>
        )}

        {/* Upload Step (Legacy - kept for backwards compatibility) */}
        {workflowStep === 'upload' && (
          <div className="max-w-4xl mx-auto px-8 py-16 fade-in">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-slate-900 mb-4">Upload Your Data</h2>
              <p className="text-lg text-slate-600">
                Import your marketing performance data to begin the analysis.
              </p>
            </div>
            <ErrorBoundary>
              <DataUploadSection
                onDataLoaded={(fileId, columns) => {
                  setDataFileId(fileId);
                  setDataColumns(columns);
                  setWorkflowStep('config');
                }}
                onError={(error) => {
                  console.error('Upload error:', error);
                }}
              />
            </ErrorBoundary>
          </div>
        )}

        {/* Config Step */}
        {workflowStep === 'config' && (
          <div className="max-w-6xl mx-auto px-8 py-12 fade-in">
            <ErrorBoundary>
              <ConfigForm
                dataFileId={dataFileId}
                dataColumns={dataColumns}
                onAnalysisStart={(jobId) => {
                  setCurrentJobId(jobId);
                  setWorkflowStep('results');
                }}
              />
            </ErrorBoundary>
          </div>
        )}

        {/* Results Step */}
        {workflowStep === 'results' && currentJobId && (
          <div className="max-w-7xl mx-auto px-8 py-12 fade-in">
            <ErrorBoundary>
              <ResultsView
                jobId={currentJobId}
                onBack={() => setWorkflowStep('config')}
              />
            </ErrorBoundary>
          </div>
        )}
      </main>

      {/* Footer */}
      {workflowStep !== 'landing' && workflowStep !== 'model-builder' && (
        <footer className="bg-white border-t border-slate-200 mt-auto">
          <div className="max-w-7xl mx-auto px-8 py-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setWorkflowStep('landing')}
                  className="btn btn-secondary"
                >
                  <Home className="w-4 h-4" />
                  Back to Home
                </button>
                {workflowStep === 'config' && (
                  <button
                    onClick={() => setWorkflowStep('upload')}
                    className="btn btn-ghost"
                  >
                    <FolderOpen className="w-4 h-4" />
                    Change Data
                  </button>
                )}
              </div>
              <div className="flex items-center gap-3 text-sm text-slate-500">
                <div className={`status-indicator ${isHealthy ? 'online' : 'offline'}`} />
                <span className="font-medium">{isHealthy ? 'API Connected' : 'API Disconnected'}</span>
                <span className="text-slate-400">|</span>
                <span className="font-mono text-xs">{backendStatus}</span>
              </div>
            </div>
          </div>
        </footer>
      )}
    </div>
  );

}

const App = () => {
  return (
    <BrowserRouter>
      <LogtoProvider config={config}>
        <Routes>
          <Route path="/callback" element={<Callback />} />
          <Route path="/" element={<Dashboard />} />
          {/* Redirect any unknown routes to home */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </LogtoProvider>
    </BrowserRouter>
  );
};

export default App;
